import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import {
  AccumulationChartComponent,
  AccumulationSeriesCollectionDirective,
  AccumulationSeriesDirective,
  AccumulationDataLabel,
  AccumulationLegend,
  AccumulationTooltip,
  Inject,
  PieSeries,
  AccumulationAnnotation,
} from '@syncfusion/ej2-react-charts';
import { Box, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { MoneyFormatv3 } from '../../../library/MoneyFormat';
import { useThemes } from '../../../components/Theme/theme';

const INITIAL_RADIUS = 80;
const DECREMENT = 3;

const calculateRadius = (index, initialRadius, decrement) => {
  return `${initialRadius - index * decrement}%`;
};

const calculateSum = (dataArray, groupNames) => {
  if (!dataArray?.length) return 0;
  return dataArray
    .filter((item) => groupNames.includes(item.group_name))
    .reduce((sum, item) => sum + (item.balance || 0), 0);
};

const calculateNetProfitLoss = (groupData) => {
  const GROUP_NAMES = {
    TRADING_INCOMES: 'Trading Incomes',
    DIRECT_INCOME: 'Direct Income',
    TRADING_EXPENSE: 'Trading Expense',
    DIRECT_EXPENSE: 'Direct Expense',
  };

  const netIncomeGroups = [GROUP_NAMES.TRADING_INCOMES, GROUP_NAMES.DIRECT_INCOME, 'Indirect Income'];
  const netExpenseGroups = [
    GROUP_NAMES.TRADING_EXPENSE,
    GROUP_NAMES.DIRECT_EXPENSE,
    'Indirect Expenses',
    'Depreciation & Amortisation',
    'Interest Expenses',
    'Tax Expenses'
  ];

  const stockData = groupData?.[0] || {};
  const closingStock = stockData.closingstockamt || 0;
  const openingStock = stockData.openingstockamt || 0;

  const netIncome = calculateSum(groupData, netIncomeGroups) + closingStock;
  const netExpense = calculateSum(groupData, netExpenseGroups) + openingStock;

  const netProfitLoss = netIncome - netExpense;

  return {
    netProfitLoss,
    isNetProfit: netProfitLoss > 0,
    isNetLoss: netProfitLoss < 0,
    netIncome,
    netExpense
  };
};

const processGroupData = (groupData) => {
  if (!groupData || groupData.length === 0) return { innerChartData: [], outerChartData: [] };

  const { netProfitLoss, isNetProfit, isNetLoss, netIncome, netExpense } = calculateNetProfitLoss(groupData);



  const incomeCategories = {
    'Trading Incomes': 'Trading Incomes',
    'Direct Income': 'Direct Income',
    'Indirect Income': 'Indirect Income'
  };

  const expenseCategories = {
    'Trading Expense': 'Trading Expense',
    'Direct Expense': 'Direct Expense',
    'Salary, Benefits & Wage Expenses': 'Salary, Benefits & Wage Expenses',
    'General & Administrative Expenses': 'General & Administrative Expenses',
    'Advertising & Promotion': 'Advertising & Promotion',
    'Professional Services': 'Professional Services',
    'Depreciation & Amortisation': 'Depreciation & Amortisation',
    'Interest Expenses': 'Interest Expenses',
    'Tax Expenses': 'Tax Expenses'
  };

  const incomeData = {};
  let totalIncome = 0;

  Object.keys(incomeCategories).forEach(category => {
    const categoryData = groupData.find(item => item.group_name === category);
    let amount = categoryData ? Math.abs(categoryData.balance) : 0;

    incomeData[category] = amount;
    totalIncome += amount;
  });

  if (isNetLoss && Math.abs(netProfitLoss) > 0) {
    incomeData['Net Loss'] = Math.abs(netProfitLoss);
    totalIncome += Math.abs(netProfitLoss);
  }

  const innerChartData = Object.keys(incomeData).map((category, index) => {
    const amount = incomeData[category];
    const percentage = totalIncome > 0 ? (amount / totalIncome) * 100 : 0;

    return {
      category: category,
      amount: amount,
      percentage: percentage
    };
  }).filter(item => {
    return item.amount > 0 && item.percentage > 0;
  });

  const netLossItem = innerChartData.find(item => item.category === 'Net Loss');
  if (netLossItem) {
    const otherItemsTotal = innerChartData
      .filter(item => item.category !== 'Net Loss')
      .reduce((sum, item) => sum + parseFloat(item.percentage.toFixed(2)), 0);
    const currentTotal = parseFloat(otherItemsTotal.toFixed(2)) + parseFloat(netLossItem.percentage.toFixed(2));
    const difference = parseFloat((100 - currentTotal).toFixed(2));
    const targetPercentage = parseFloat((netLossItem.percentage + difference).toFixed(2));
    const ratioAdjustment = targetPercentage / netLossItem.percentage;

    netLossItem.amount = parseFloat((netLossItem.amount * ratioAdjustment).toFixed(2));
    netLossItem.percentage = targetPercentage;

    if (netLossItem.percentage < 0) {
      netLossItem.percentage = 0.01;
      netLossItem.amount = 0.01;
    }
  }

  const expenseData = {};
  let totalExpenses = 0;

  Object.keys(expenseCategories).forEach(category => {
    const categoryData = groupData.find(item => item.group_name === category);
    let amount = 0;

    if (category === 'Trading Expense') {
      const tradingExpenseData = categoryData;
      const purchaseData = groupData.find(item => item.group_name === 'Purchases');

      if (tradingExpenseData) {
        const openingStock = tradingExpenseData.openingstockamt || 0;
        const purchase = purchaseData ? Math.abs(purchaseData.balance) : 0;
        const closingStock = tradingExpenseData.closingstockamt || 0;

        amount = openingStock + purchase - closingStock;

        if (amount <= 0) {
          amount = 0;
        }
      } else {
        amount = categoryData ? Math.abs(categoryData.balance) : 0;
      }
    } else {
      amount = categoryData ? Math.abs(categoryData.balance) : 0;
    }



    expenseData[category] = amount;
    totalExpenses += amount;
  });

  const indirectExpenseSubGroups = groupData.filter(item =>
    item.group_path && item.group_path.startsWith('/25/') && item.group_name !== 'Indirect Expenses' && item.group_name !== 'Indirect Income'
  );

  const processedCategories = Object.keys(expenseData);

  indirectExpenseSubGroups.forEach(item => {
    if (!processedCategories.includes(item.group_name)) {
      const amount = Math.abs(item.balance);
      expenseData[item.group_name] = amount;
      totalExpenses += amount;
    }
  });


  if (isNetProfit && netProfitLoss > 0) {
    expenseData['Net Profit'] = netProfitLoss;
    totalExpenses += netProfitLoss;
  }


  const expenseEntries = Object.keys(expenseData)
    .map(category => {
      let amount = expenseData[category];

      if ((category === 'Trading Expense' || category === 'General & Administrative Expenses') && amount === 0) {
        amount = 0;
      }

      return {
        label: category,
        amount: amount,
        percentage: totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0
      };
    })
    .filter(item => {
      const hasValidAmount = item.amount > 0 || item.label === 'Trading Expense' || item.label === 'General & Administrative Expenses';
      const hasValidPercentage = item.percentage > 0;
      const isNotPurchases = item.label !== 'Purchases';
      return hasValidAmount && hasValidPercentage && isNotPurchases;
    })
    .sort((a, b) => b.amount - a.amount);

  const largeSlices = expenseEntries.filter(item => item.percentage > 8);
  const smallSlices = expenseEntries.filter(item => item.percentage <= 8);

  const sortedExpenseEntries = [...expenseEntries].sort((a, b) => b.percentage - a.percentage);
  const top7Items = sortedExpenseEntries.slice(0, 7);
  const remainingItems = sortedExpenseEntries.slice(7);

  const othersItems = remainingItems.filter(item => item.label !== 'Net Profit');
  const netProfitItem = remainingItems.find(item => item.label === 'Net Profit');

  let processedExpenseEntries = [...top7Items];

  if (othersItems.length > 0) {
    const othersTotal = othersItems.reduce((sum, item) => sum + item.amount, 0);
    const othersPercentage = othersItems.reduce((sum, item) => sum + item.percentage, 0);

    processedExpenseEntries.push({
      label: 'Others',
      amount: othersTotal,
      percentage: othersPercentage,
      isOthers: true
    });
  }

  if (netProfitItem) {
    processedExpenseEntries.push(netProfitItem);
  }

  const finalNetProfit = processedExpenseEntries.find(item => item.label === 'Net Profit');
  if (finalNetProfit) {
    const otherItemsTotal = processedExpenseEntries
      .filter(item => item.label !== 'Net Profit')
      .reduce((sum, item) => sum + parseFloat(item.percentage.toFixed(2)), 0);

    const currentTotal = parseFloat(otherItemsTotal.toFixed(2)) + parseFloat(finalNetProfit.percentage.toFixed(2));
    const difference = parseFloat((100 - currentTotal).toFixed(2));
    const targetPercentage = finalNetProfit.percentage + difference;
    const ratioAdjustment = targetPercentage / finalNetProfit.percentage;

    finalNetProfit.amount = parseFloat((finalNetProfit.amount * ratioAdjustment).toFixed(2));
    finalNetProfit.percentage = targetPercentage;

    if (finalNetProfit.percentage < 0) {
      finalNetProfit.percentage = 0.01;
    }
  }

  const outerChartData = processedExpenseEntries.map((item, index) => ({
    ...item,
    radius: calculateRadius(index, INITIAL_RADIUS, DECREMENT),
    isLargeSlice: item.percentage > 8
  }));

  const hasValidData = innerChartData.length > 0 || outerChartData.length > 0;
  if (!hasValidData) {
    return { innerChartData: [], outerChartData: [] };
  }

  const allAmountsZero =
    [...innerChartData, ...outerChartData].every(item => item.amount === 0 || item.amount === undefined);

  if (allAmountsZero) {
    return { innerChartData: [], outerChartData: [] };
  }

  return { innerChartData, outerChartData };
};

const PieChart = ({ year, id, groupData, loading = true, title, isColumnLayout = false }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const chartHeight = useMemo(() => {
    if (isXs) return '400px';
    if (isSm) return '500px';
    return '600px';
  }, [isXs, isSm]);

  const dummyChartHeight = useMemo(() => {
    if (isXs) return '420px';
    if (isSm) return '520px';
    return '620px';
  }, [isXs, isSm]);

  const datalabel = useMemo(() => ({
    visible: true,
    position: 'Inside',
    textWrap: 'Wrap',
    connectorStyle: { length: '20px', width: 1, color: 'black' },
    name: 'text',
  }), []);

  const innerDatalabel = useMemo(() => ({
    visible: true,
    position: 'Inside',
    textWrap: 'Wrap',
    connectorStyle: { length: '10px', width: 1 },
    enableRotation: false,
    name: 'text',
  }), []);

  const datalabelDummy = useMemo(() => ({
    visible: true,
    position: 'Inside',
    maxWidth: 100,
    textWrap: 'Wrap',
    connectorStyle: { length: '1px', width: 1 },
    name: 'text',
  }), []);
  const { theme: customTheme } = useThemes();

  const themeValues = useMemo(() => ({
    isDarkTheme: customTheme?.palette?.TmId?.id?.toString().includes('9'),
    textColor: customTheme?.palette?.TmId?.id?.toString().includes('9') ? '#ffff' : '#080000',
    noDataColor: customTheme?.palette?.TmId?.id?.toString().includes('9') ? 'rgba(255,255,255,0.6)' : 'rgba(8,0,0,0.6)'
  }), [customTheme?.palette?.TmId?.id]);


  const { innerChartData: rawInnerChartData, outerChartData } = useMemo(() => {
    return processGroupData(groupData);
  }, [groupData]);






  const paletteArrays = useMemo(() => {
    const paletteSetOne = ['#FDB033', '#ff7b00', '#ff8800', '#ff9500', '#FB8C00', '#FC9E1A', '#FDB033', '#FEC24C'];
    const paletteSetTwo = ['#911B7C', '#9D2B84', '#A93B8C', '#B54B94', '#C15B9C', '#CD6BA4', '#D97BAC', '#E58BB4'];
    const dummyPaletteSetTwo = ['#C88DBE', '#CE96C2', '#D49FC6', '#DAA8CA', '#E0B1CE', '#E6BAD2', '#EBBDD6', '#F1C6DA'];
    const dummyPaletteSetOne = ['#E8B980', '#E8BA82', '#E8BC85', '#FCC480', '#FDD09D', '#FED9B3', '#FEE5C0'];
    const innerPaletteSetOne = ['#f79d65', '#ff7b00', '#ff8800', '#f7b267', '#f79d65', '#DA70D6', '#741E71', '#AE388B'];
    const innerPaletteSetTwo = ['#cbdb47', '#fdc77b', '#d8572a', '#f6863d'];

    return {
      outerPalettes: id === 'chartdiv1' ? [...paletteSetOne].reverse() : [...paletteSetTwo].reverse(),
      innerPalettes: id === 'chartdiv1' ? [...innerPaletteSetTwo].reverse() : [...innerPaletteSetOne].reverse(),
      dummyPalettes: id === 'chartdiv1' ? [...dummyPaletteSetOne].reverse() : [...dummyPaletteSetTwo].reverse()
    };
  }, [id]);

  const innerChartData = useMemo(() => {
    return rawInnerChartData.map((item, index) => {
      const oppositePalette = paletteArrays.innerPalettes || [];

      const alphabetLabel = String.fromCharCode(97 + index);
      const percentageLabel = `${item.percentage.toFixed(2)}%`;

      const color = oppositePalette.length > 0
        ? oppositePalette[index % oppositePalette.length]
        : '#999999';

      return {
        ...item,
        color,
        alphabetLabel: percentageLabel,
        legendLabel: alphabetLabel
      };
    });
  }, [rawInnerChartData, paletteArrays.innerPalettes]);

  const totals = useMemo(() => {
    const { netProfitLoss, isNetProfit, isNetLoss, netIncome, netExpense } = calculateNetProfitLoss(groupData || []);
    return { netProfitLoss, isNetProfit, isNetLoss, netIncome, netExpense };
  }, [groupData]);

  const textRender = useCallback((args) => {
    const percentage = args.point.percentage ? args.point.percentage.toFixed(2) : '0.00';

    if (parseFloat(percentage) === 0) {
      args.text = '';
      return;
    }

    args.font.fontStyle = 'roboto';

    let label = args.point.x || '';
    const labelLength = label.length;
    const isInnerChart = args.series.name === 'Income';

    if (isInnerChart) {
      if (parseFloat(percentage) < 3) {
        args.text = '';
        return;
      }
      if (args.point.labelPosition !== 'Inside') {
        args.point.labelPosition = 'Inside';
      }
      args.font.color = 'white';
      args.text = `${percentage}%`;
      args.font.size = '12.5px';
    } else {
      const screenWidth = window.innerWidth;

      let fontSize = '12px';
      if (labelLength > 30) {
        fontSize = '10px';
      } else if (labelLength > 25) {
        fontSize = '11px';
      } else if (screenWidth < 600) {
        fontSize = '10px';
      } else if (screenWidth < 900) {
        fontSize = '12px';
      }

      args.font.size = fontSize;
      args.font.fontWeight = 'bold';

      const isLargeSlice = args.point.percentage > 8;
      const isMediumSlice = args.point.percentage <= 8 && args.point.percentage > 2;

      let shouldBeInside = false;

      if (isLargeSlice) {
        shouldBeInside = labelLength <= 27;
      } else if (isMediumSlice) {
        shouldBeInside = labelLength <= 22;
      }

      const desiredPosition = shouldBeInside ? 'Inside' : 'Outside';
      if (args.point.labelPosition !== desiredPosition) {
        args.point.labelPosition = desiredPosition;
      }

      if (shouldBeInside) {
        args.text = `${label}<br>${percentage}%`;
      } else {
        let displayLabel = label;
        if (labelLength > 30) {
          const midPoint = Math.floor(labelLength / 2);
          const spaceBefore = label.lastIndexOf(' ', midPoint);
          const spaceAfter = label.indexOf(' ', midPoint);
          let breakPoint = midPoint;
          if (spaceBefore > labelLength * 0.3) {
            breakPoint = spaceBefore + 1;
          } else if (spaceAfter > 0 && spaceAfter < labelLength * 0.7) {
            breakPoint = spaceAfter + 1;
          }
          displayLabel = `${label.substring(0, breakPoint)}<br>${label.substring(breakPoint)}`;
        }
        args.text = `${displayLabel}<br>${percentage}%`;
      }

      args.font.color = '#000000';
    }
  }, []);







  const tooltipRender = useCallback((args) => {
    const isInnerChart = args.series.name === 'Income';
    const chartType = isInnerChart ? 'Income' : 'Expense';

    args.headerText = `${chartType} Details`;
    args.text = `${args.point.x}: ${MoneyFormatv3(args.point.y)}`;
  }, []);


  const dummytextRender = (args) => {
    args.font.fontWeight = 'bold';
    args.font.fontStyle = 'roboto';
    const screenWidth = window.innerWidth;
    let fontSize = '11px';
    if (screenWidth < 600) {
      fontSize = '9px';
    } else if (screenWidth < 900) {
      fontSize = '10px';
    }
    args.font.opacity = 0.3;
    args.text = `${args.point.x}`;
  };

  const animationConfig = useMemo(() => ({
    enable: true,
    duration: 2000,
    delay: 500,
    easing: 'EaseOut',
  }), []);

  const marginConfig = useMemo(() => ({
    top: 20,
    bottom: 20,
    left: 20,
    right: 20
  }), []);

  const marginConfigLarge = useMemo(() => ({
    top: 60,
    bottom: 60,
    left: 80,
    right: 80
  }), []);

  const innerTooltipConfig = useMemo(() => ({
    enable: false,
    format: '${point.x}: ${point.y}',
    header: 'Income Details',
    enableAnimation: false,
  }), []);

  const outerTooltipConfig = useMemo(() => ({
    enable: true,
    format: '${point.x}: ${point.y}',
    header: 'Expense Details',
    enableAnimation: false,
    fill: '#ffffff',
    textStyle: {
      color: '#000000',
      fontFamily: 'Roboto',
      fontWeight: 'normal',
      size: '14px'
    },
    border: {
      color: '#cccccc',
      width: 1
    },
    enableShadow: false,
  }), []);

  const innerOnlyTooltipConfig = useMemo(() => ({
    enable: true,
    format: '${point.x}: ${point.y}',
    header: 'Income Details',
    enableAnimation: false,
  }), []);

  const hiddenLegendSettings = useMemo(() => ({ visible: false }), []);
  const pointerEventsStyle = useMemo(() => ({ pointerEvents: 'auto' }), []);

  const outerDataLabel = useMemo(() => ({
    ...datalabel,
    position: 'Outside',
  }), []);

  const titleGradient = useMemo(() => {
    if (id === 'chartdiv1') {
      return 'linear-gradient(135deg, #FDB033 0%, #ff7b00 50%, #FB8C00 100%)';
    } else {
      return 'linear-gradient(135deg, #911B7C 0%, #B54B94 50%, #D97BAC 100%)';
    }
  }, [id]);

  const titleAccentColor = useMemo(() => {
    return id === 'chartdiv1' ? '#FDB033' : '#911B7C';
  }, [id]);

  return (
    <Box sx={{ borderRadius: 2, backgroundColor: 'transparent', overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isColumnLayout ? 'flex-start' : (id === 'chartdiv2' ? 'flex-end' : 'flex-start'),
          justifyContent: 'flex-start',
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1, sm: 1.5 },
          mb: { xs: 0.5, sm: 1 },
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5 },
              position: 'relative',
              pb: 1,
              alignSelf: isColumnLayout ? 'flex-start' : (id === 'chartdiv2' ? 'flex-end' : 'flex-start'),
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                <Box
                  sx={{
                    width: { xs: 10, sm: 12 },
                    height: { xs: 10, sm: 12 },
                    borderRadius: '50%',
                    background: titleGradient,
                    boxShadow: `0 2px 12px ${titleAccentColor}60, 0 0 0 4px ${titleAccentColor}15`,
                    flexShrink: 0,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: { xs: 4, sm: 5 },
                      height: { xs: 4, sm: 5 },
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      opacity: 0.9,
                    },
                  }}
                />
                <Typography
                  sx={{
                    color: themeValues.textColor,
                    fontWeight: '600',
                    fontSize: { xs: '11px', sm: '12px', md: '15px' },
                    letterSpacing: '0.01em',
                    position: 'relative',
                    lineHeight: 1.4,
                  }}
                >
                  {title || 'FY: Not selected'}
                </Typography>
              </Box>

              <Box
                sx={{
                  position: 'relative',
                  height: '2px',
                  width: { xs: '80px', sm: '100px', md: '120px' },
                  ml: { xs: 1, sm: 3 },
                  mt: -0.2,
                  alignSelf: isColumnLayout ? 'flex-start' : (id === 'chartdiv2' ? 'flex-end' : 'flex-start'),
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    background: `linear-gradient(90deg, ${titleAccentColor} 0%, transparent 100%)`,
                    borderRadius: '2px',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    right: -8,
                    top: -2,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: titleAccentColor,
                    boxShadow: `0 0 8px ${titleAccentColor}80`,
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: 2, mb: 0 }}>
        {innerChartData?.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: { xs: 1, sm: 1.5, md: 2 },
              alignItems: 'center',
            }}
          >
            {innerChartData.map((item, idx) => (
              <Box
                key={`income-${idx}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: { xs: 10, sm: 12, md: 14 },
                    height: { xs: 10, sm: 12, md: 14 },

                    borderRadius: '50%',
                    bgcolor: item.color || '#999',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    color: themeValues.textColor,
                    fontSize: { xs: '11px', sm: '12px', md: '13px' },
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.category} <Box component="span" sx={{ opacity: 0.7, fontWeight: '600' }}>
                    ({item.percentage.toFixed(2)}%)
                  </Box>
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2, mt: 0 }}>
        {(innerChartData?.length > 0 || outerChartData?.length > 0) ? (
          <Box
            sx={{
              position: 'relative',
              height: chartHeight,
              width: '100%',
            }}
          >
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 1, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.15))' }}>
              <AccumulationChartComponent
                id={`${id}-outer`}
                background={'transparent'}
                enableAnimation={true}
                height={chartHeight}
                width={'100%'}
                tooltip={outerTooltipConfig}
                textRender={textRender}
                tooltipRender={tooltipRender}
                legendSettings={hiddenLegendSettings}
                margin={marginConfig}
                style={{ pointerEvents: 'auto' }}
              >
                <Inject services={[PieSeries, AccumulationLegend, AccumulationTooltip, AccumulationDataLabel, AccumulationAnnotation]} />
                <AccumulationSeriesCollectionDirective>
                  <AccumulationSeriesDirective
                    name={'Expense'}
                    type={'Pie'}
                    dataSource={outerChartData}
                    xName={'label'}
                    yName={'amount'}
                    innerRadius={'32%'}
                    radius='radius'
                    explode={true}
                    explodeOffset={'6%'}
                    startAngle={90}
                    endAngle={450}
                    palettes={paletteArrays.outerPalettes}
                    border={{ width: 1, color: '#ffffff' }}
                    dataLabel={{ ...outerDataLabel, visible: true }}
                    animation={animationConfig}
                  />
                </AccumulationSeriesCollectionDirective>
              </AccumulationChartComponent>
            </Box>

            <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
              <AccumulationChartComponent
                id={`${id}-inner`}
                background={'transparent'}
                enableAnimation={true}
                height={chartHeight}
                width={'100%'}
                tooltip={innerTooltipConfig}
                textRender={textRender}
                legendSettings={hiddenLegendSettings}
                margin={marginConfig}
                style={{ pointerEvents: 'none' }}
              >
                <Inject services={[PieSeries, AccumulationLegend, AccumulationTooltip, AccumulationDataLabel, AccumulationAnnotation]} />
                <AccumulationSeriesCollectionDirective>
                  <AccumulationSeriesDirective
                    name={'Income'}
                    type={'Pie'}
                    dataSource={innerChartData}
                    xName={'category'}
                    yName={'amount'}
                    radius={'28%'}
                    explode={false}
                    pointColorMapping={'color'}
                    startAngle={90}
                    endAngle={450}
                    border={{ width: 1, color: '#ffffff' }}
                    dataLabel={{ ...innerDatalabel, visible: true }}
                    animation={animationConfig}
                  />
                </AccumulationSeriesCollectionDirective>
              </AccumulationChartComponent>
            </Box>
          </Box>
        ) : (
          <>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: { xs: 400, sm: 500, md: 600 } }}>
                <Skeleton variant="circular" width={{ xs: 200, sm: 250, md: 300 }} height={{ xs: 200, sm: 250, md: 300 }} />
              </Box>
            ) : (
              <Box
                sx={{
                  position: 'relative',
                  height: dummyChartHeight,
                  width: '100%',
                }}
              >
                <Box sx={{ position: 'absolute', inset: 0, zIndex: 1, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.15))' }}>
                  <AccumulationChartComponent
                    id={`${id}-dummy`}
                    background={'transparent'}
                    enableAnimation={true}
                    height={dummyChartHeight}
                    width={'100%'}
                    legendSettings={hiddenLegendSettings}
                    textRender={dummytextRender}
                    margin={marginConfig}
                  >
                    <Inject services={[PieSeries, AccumulationLegend, AccumulationTooltip, AccumulationDataLabel, AccumulationAnnotation]} />
                    <AccumulationSeriesCollectionDirective>
                      <AccumulationSeriesDirective
                        name={'No Data'}
                        type={'Pie'}
                        dataSource={[
                          { category: 'Trading expenses', amount: 150, radius: calculateRadius(0, 80, DECREMENT) },
                          { category: 'Direct Expenses', amount: 85, radius: calculateRadius(1, 80, DECREMENT) },
                          { category: 'Salary, Benefits & Wage Expenses', amount: 80, radius: calculateRadius(2, 80, DECREMENT) },
                          { category: 'General & Administrative Expenses', amount: 75, radius: calculateRadius(3, 80, DECREMENT) },
                          { category: 'Depreciation & Amortisation', amount: 60, radius: calculateRadius(4, 80, DECREMENT) },
                          { category: 'Interest Expenses', amount: 45, radius: calculateRadius(5, 80, DECREMENT) },
                          { category: 'Tax Expenses', amount: 30, radius: calculateRadius(6, 80, DECREMENT) },
                        ]}
                        xName={'category'}
                        yName={'amount'}
                        innerRadius={'28%'}
                        radius={'radius'}
                        palettes={paletteArrays.dummyPalettes}
                        border={{ width: 1, color: '#ffffff' }}
                        dataLabel={datalabelDummy}
                        animation={animationConfig}
                      />
                    </AccumulationSeriesCollectionDirective>
                  </AccumulationChartComponent>
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    zIndex: 2,
                    background: themeValues.isDarkTheme
                      ? 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 70%, transparent 100%)'
                      : 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 70%, transparent 100%)',
                    borderRadius: '50%',
                  }}
                >
                  <Box
                    sx={{
                      px: { xs: 3, sm: 4 },
                      py: { xs: 2, sm: 2.5 },
                      opacity: 90,
                    }}
                  >
                    <Typography
                      sx={{
                        color: themeValues.noDataColor,
                        fontSize: { xs: '12px', sm: '15px', md: '18px' },
                        fontWeight: '700',
                        textShadow: themeValues.isDarkTheme
                          ? '0 2px 8px rgba(0, 0, 0, 0.5)'
                          : '0 2px 4px rgba(0, 0, 0, 0.1)',
                        mb: 1,
                        textAlign: 'center',
                        letterSpacing: '0.02em',
                      }}
                    >
                      No Data Available
                    </Typography>
                    <Typography
                      sx={{
                        color: themeValues.noDataColor,
                        fontSize: { xs: '12px', sm: '13px', md: '14px' },
                        opacity: 0.75,
                        textAlign: 'center',
                        maxWidth: '240px',
                        fontWeight: '500',
                      }}
                    >
                      Please select a period to view chart data
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default PieChart;
